import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import LocalizedLink from "../components/LocalizedLink";
import SEO from "../components/SEO";
import Layout from "../components/Layout";
import locales from "../constants/locales";
import { fillFiguresDeep } from "../utils/keyFigures";
import { getIcon } from "../components/icons/lineIcons";

const LandingNew = ({ pageContext: { locale }, data }) => {
  const landing = data.markdownRemark;
  // {{key}} placeholders are filled from src/data/keyFigures.json
  const fm = fillFiguresDeep(landing.frontmatter, locale);

  return (
    <Layout locale={locale}>
      <SEO
        title={`TriDyme | ${fm.title}`}
        lang={locale}
        url={
          locales[locale].default
            ? `/${fm.slug}`
            : `/${locales[locale].path}/${fm.slug}`
        }
        image={fm.seo?.featuredImage?.publicURL}
        description={fm.seo?.description}
        author={fm.seo?.author}
      />

      <div className="landing-page">
        {/* HERO SECTION */}
        {fm.hero && (
          <section className="hero-landing">
            <div className="hero-bg"></div>
            <div className="hero-grid"></div>

            <div className="hero-container">
              <div className="hero-content">
                <div className="hero-badge">
                  <span className="hero-badge-dot"></span>
                  <span>{fm.title}</span>
                </div>
                <h1>
                  {fm.hero.title}
                  {fm.hero.animated_words && (
                    <span className="highlight">
                      {fm.hero.animated_words.split(',')[0]}
                    </span>
                  )}
                </h1>
                <p className="hero-subtitle">{fm.hero.paragraph}</p>

                {fm.hero.buttons && (
                  <div className="hero-cta">
                    {fm.hero.buttons.map((btn, idx) => {
                      const btnClass = btn.style === 'primary'
                        ? 'btn-landing btn-primary btn-large'
                        : 'btn-landing btn-outline btn-large';

                      if (btn.type === 'external') {
                        return (
                          <a key={idx} href={btn.link} className={btnClass}>
                            {btn.button}
                          </a>
                        );
                      }
                      return (
                        <LocalizedLink key={idx} to={btn.link} className={btnClass}>
                          {btn.button}
                        </LocalizedLink>
                      );
                    })}
                  </div>
                )}

                {fm.hero.stats && (
                  <div className="hero-stats">
                    {fm.hero.stats.map((stat, idx) => (
                      <div key={idx} className="stat">
                        <span className="stat-value">{stat.value}</span>
                        <span className="stat-label">{stat.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="hero-visual">
                <div className="hero-mockup">
                  <div className="mockup-header">
                    <span className="mockup-dot"></span>
                    <span className="mockup-dot"></span>
                    <span className="mockup-dot"></span>
                  </div>
                  {fm.hero.covers?.length > 0 && fm.hero.covers[0]?.cover?.childImageSharp ? (
                    <div className="mockup-screenshot">
                      <img src={fm.hero.covers[0].cover.childImageSharp.fluid.src} alt={fm.title} />
                    </div>
                  ) : (
                    <div className="mockup-content">
                      {fm.services?.service?.slice(0, 6).map((service, idx) => (
                        <div key={idx} className="mockup-card">
                          <div className="mockup-icon">
                            {getIcon(service.icon)}
                          </div>
                          <div className="mockup-title">{service.title}</div>
                          <div className="mockup-desc">{service.paragraph?.substring(0, 40)}...</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SERVICES/CHALLENGES SECTION */}
        {fm.services && (
          <section className="features-landing" id="services">
            <div className="features-container">
              <div className="section-header">
                <span className="section-tag">{fm.title}</span>
                <h2 className="section-title">{fm.services.title}</h2>
                <p className="section-subtitle">{fm.services.paragraph}</p>
              </div>
              <div className="features-grid">
                {fm.services.service?.map((service, idx) => (
                  <div key={idx} className="feature-card">
                    <div className="feature-icon">
                      {getIcon(service.icon)}
                    </div>
                    <h3 className="feature-title">{service.title}</h3>
                    <p className="feature-desc">{service.paragraph}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FEATURES/SOLUTION SECTION */}
        {fm.features && (
          <section className="ai-section" id="features">
            <div className="ai-container">
              <div className="ai-content">
                <div className="ai-text">
                  <h2>{fm.features.title}</h2>
                  <p>{fm.features.paragraph}</p>

                  <div className="ai-steps">
                    {fm.features.list?.map((item, idx) => (
                      <div key={idx} className="ai-step">
                        <div className="ai-step-number">{idx + 1}</div>
                        <div className="ai-step-content">
                          <p>{item.paragraph}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="ai-visual">
                  {fm.features.cover?.childImageSharp ? (
                    <div className="ai-screenshot">
                      <img src={fm.features.cover.childImageSharp.fluid.src} alt={fm.features.title} />
                    </div>
                  ) : (
                    <div className="ai-diagram">
                      {fm.features.feature?.map((feat, idx) => (
                        <div key={idx} className="diagram-node">
                          <div className="diagram-node-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                              <polyline points="22,4 12,14.01 9,11.01" />
                            </svg>
                          </div>
                          <span>
                            <strong>{feat.symbol}{feat.number}</strong> {feat.paragraph}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* BENEFITS SECTION */}
        {fm.benefits && fm.benefits.length > 0 && (
          <section className="features-landing" id="benefits">
            <div className="features-container">
              {fm.benefits.map((benefit, bidx) => (
                <div key={bidx} style={{ marginBottom: '4rem' }}>
                  <div className="section-header">
                    {benefit.icon && (
                      <div className="section-icon" aria-hidden="true">{getIcon(benefit.icon)}</div>
                    )}
                    <h2 className="section-title">{benefit.title}</h2>
                    <p className="section-subtitle">{benefit.paragraph}</p>
                  </div>
                  {benefit.cover?.childImageSharp && (
                    <div className="ai-screenshot" style={{ marginBottom: '2rem' }}>
                      <img src={benefit.cover.childImageSharp.fluid.src} alt={benefit.title} />
                    </div>
                  )}
                  <div className="features-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                    {benefit.list?.map((item, idx) => (
                      <div key={idx} className="feature-card">
                        <div className="feature-icon">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                            <polyline points="22,4 12,14.01 9,11.01" />
                          </svg>
                        </div>
                        <p className="feature-desc">{item.paragraph}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA SECTION */}
        {fm.get_started && (
          <section className="cta-section">
            <div className="cta-container">
              <h2>{fm.get_started.title}</h2>
              <p>{fm.get_started.paragraph}</p>
              <div className="cta-buttons">
                {fm.get_started.buttons?.map((btn, idx) => {
                  const btnClass = btn.style === 'primary'
                    ? 'btn-landing btn-black btn-large'
                    : 'btn-landing btn-outline btn-large';

                  if (btn.type === 'external') {
                    return (
                      <a key={idx} href={btn.link} className={btnClass}>
                        {btn.button}
                      </a>
                    );
                  }
                  return (
                    <LocalizedLink key={idx} to={btn.link} className={btnClass}>
                      {btn.button}
                    </LocalizedLink>
                  );
                })}
              </div>
            </div>
          </section>
        )}

      </div>
    </Layout>
  );
};

LandingNew.propTypes = {
  pageContext: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }).isRequired,
};

export default LandingNew;

export const query = graphql`
  query ($slug: String!, $localeRegex: String!) {
    markdownRemark(frontmatter: { slug: { eq: $slug }, locale: { regex: $localeRegex } }) {
      html
      frontmatter {
        slug
        title
        date(formatString: "DD-MM-YYYY")
        seo {
          featuredImage {
            publicURL
          }
          author
          description
        }
        hero {
          title
          paragraph
          animated_words
          covers {
            cover {
              childImageSharp {
                fluid(maxWidth: 1200, quality: 90) {
                  src
                }
              }
            }
          }
          buttons {
            button
            link
            type
            style
          }
          stats {
            value
            label
          }
        }
        services {
          title
          paragraph
          service {
            icon
            title
            paragraph
          }
        }
        features {
          title
          paragraph
          cover {
            childImageSharp {
              fluid(maxWidth: 1200, quality: 90) {
                ...GatsbyImageSharpFluid
              }
            }
          }
          list {
            paragraph
          }
          feature {
            symbol
            number
            paragraph
          }
        }
        benefits {
          icon
          title
          cover {
            childImageSharp {
              fluid(maxWidth: 1200, quality: 90) {
                ...GatsbyImageSharpFluid
              }
            }
          }
          paragraph
          list {
            paragraph
          }
        }
        get_started {
          title
          paragraph
          buttons {
            button
            link
            type
            style
          }
        }
      }
      fields {
        slug
      }
    }
  }
`;
